// components/LogSessionWrapper.tsx
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useLogSession } from "../contexts/LogSessionContext";
import { useLang } from "../contexts/LangContext";
import fishMealIcon from "../assets/images/icons/fish-meal.png";
import { convertInCurrentLang, getLocalDateString } from "../utils/misc";
import { useEffect } from "react";
import { PLATFORM_UI_CONFIG } from "../config/app.config";
import { logService } from "../services/log.service";


export default function LogSessionWrapper() {
  const {
    mode,
    person,
    user,
    currentDayRecord,
    setcurrentDayRecord,
    currentMealRecord,
    setcurrentMealRecord,
    newFishItem,
    setNewFishItem,
    exitFallback,
    setExitFallback,
  } = useLogSession();
  const { lang, t } = useLang();
  const { date } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const stickyHeaderTop = PLATFORM_UI_CONFIG.getStatusBarPadding();

  const ref = searchParams.get("ref") || null;

  const hasPending =
    !!currentMealRecord ||
    (currentDayRecord ? (currentDayRecord.meals?.length ?? 0) > 0 : false);

  useEffect(() => {
    if (date && !currentDayRecord) {
      const formatted = getLocalDateString(new Date(date));

      const existingRecord = person.records.find(
        (record) => record.date === formatted
      );

      setcurrentDayRecord(
        existingRecord || {
          date: formatted,
          meals: [],
          surveyed_on: new Date().toISOString(),
        }
      );

      if (ref) {
        logService.info('Log Session', 'Processing ref parameter', { ref, personId: person.id, date });
        if (ref === "date_log" && person && date && setExitFallback) {
          const fallbackPath = `/log-history/${person.id}/${date}`;
          setExitFallback(fallbackPath);
          logService.info('Log Session', 'Exit fallback set', { fallbackPath });
        }
      }

      navigate("which-meal", { replace: true });
      return;
    }
  }, [date]);

  const handleClose = () => {
    if (hasPending) {
      const ok = window.confirm(
        t("confirmDiscardLog") ||
          "Discard current log? Your unsubmitted changes will be lost."
      );
      if (!ok) return;
    }

    setcurrentDayRecord(null);
    setcurrentMealRecord(null);
    setNewFishItem(null);
    
    const destination = exitFallback || (mode === "survey" ? "/survey/person-list" : "/user");
    
    // Clear forward history by replacing all survey entries
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', function preventBack() {
      window.history.pushState(null, '', destination);
      window.removeEventListener('popstate', preventBack);
      navigate(destination, { replace: true });
    });
    
    navigate(destination, { replace: true });
  };

  const title =
    mode === "survey" ? (
      <span>
        <span className="font-light">{t("surveying") || "Surveying"}: </span>
        <span className="font-bold">
          {person.name || t("unnamed") || "Unnamed"}
        </span>
      </span>
    ) : (
      <span className="font-semibold">{t("newMealLog") || "New meal log"}</span>
    );

  const isSubmittedPage = window.location.pathname.endsWith("/submitted");

  return (
    <div className="log-session-wrapper grow flex flex-col">
      {/* Sticky header */}
      <header className="sticky z-20 bg-white/80 backdrop-blur border-b border-gray-100"
        style={{ top: stickyHeaderTop }}>
        <div className="w-full px-scr py-3 flex items-center justify-between gap-3">
          <div className="flex items-center justify-start gap-2">
            {!isSubmittedPage && (
              <button className="p-1 rounded-full hover:bg-gray-50"
                onClick={() => { navigate(-1); }}
                aria-label={t("back") || "Back"}
                title={t("back") || "Back"}
              >
                {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                </svg> */}
                <svg xmlns="http://www.w3.org/2000/svg" width="1.25em" height="1.25em" viewBox="0 0 24 24"><path fill="currentColor" d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25z" /></svg>

              </button>
            )}
            <h2 className="text-lg font-semibold truncate">{title}</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center rounded-full border px-3 py-1 text-sm hover:bg-gray-50"
            aria-label={t("close") || "Close"}
            title={t("close") || "Close"}
          >
            {t("close") || "Close"}
            <span className="ml-1">✕</span>
          </button>
        </div>

        {currentDayRecord && (
          <div
            className={`flex items-center ${
              currentMealRecord ? "justify-between" : "justify-center"
            } text-center text-sm text-gray-600 font-semibold mb-2 px-scr`}
          >
            <div className="flex items-center gap-2 bg-transparent border border-gray-400 rounded-full px-3 py-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                />
              </svg>

              {convertInCurrentLang(lang, currentDayRecord.date)}
            </div>

            {/* {newFishItem && (
              <div className="flex items-center gap-2 bg-transparent border border-gray-400 rounded-full px-3 py-1">
                <span>{newFishItem.fish_name}</span>
              </div>
            )} */}

            {currentMealRecord && (
              <div className="flex items-center gap-2 bg-transparent border border-gray-400 rounded-full px-3 py-1">
                <span>{t(`mealType_${currentMealRecord.meal_type}`)}</span>
                <img
                  src={fishMealIcon}
                  alt="Meal type icon"
                  className="size-6"
                />
              </div>
            )}
          </div>
        )}
      </header>
      <div className="log-session-content w-full flex flex-col grow">
        <Outlet />
      </div>
    </div>
  );
}
